/** Phone numbers, and what answers each one: an agent or a Call Script app, never both (E08-T06). */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { data, type Schemas } from "../api/client";
import { Page, Section, useApiQuery } from "../app/Page";
import { PhoneLineDialog, useHasLine } from "../app/PhoneLineDialog";
import { useBff, useCan, useMe } from "../app/session";
import { useT } from "../i18n";
import { Button } from "../ui/Button";
import { Empty, ErrorState } from "../ui/states";
import { Table } from "../ui/Table";

type PhoneNumber = Schemas["NumberOut"];
interface Named { id: string; name: string }

/** "agt_…" or "scr_…": what answers the number, as the select's value. */
const targetOf = (n: PhoneNumber) => n.script_app_id ?? n.agent_id ?? "";

export function NumbersPage() {
  const t = useT();
  const canWrite = useCan("telephony:write");
  const numbers = useApiQuery("phone-numbers", (api) => api.GET("/v1/phone-numbers", { params: { query: { limit: 100 } } }));
  const agents = useApiQuery("agents", (api) => api.GET("/v1/agents", { params: { query: { limit: 100 } } }));
  const scripts = useApiQuery("scripts", (api) => api.GET("/v1/script-apps", { params: { query: { limit: 100 } } }));
  const agentList: Named[] = agents.data?.data ?? [];
  const scriptList: Named[] = scripts.data?.data ?? [];
  const hasLine = useHasLine(); // V1.2-UX-T06: a number needs a line first
  const [needsLine, setNeedsLine] = useState(false);
  const answeredBy = (n: PhoneNumber) => {
    if (n.script_app_id) {
      const name = scriptList.find((s) => s.id === n.script_app_id)?.name ?? n.script_app_id;
      return t("numbers.byScript", { name });
    }
    if (n.agent_id) return t("numbers.byAgent", { name: agentList.find((a) => a.id === n.agent_id)?.name ?? n.agent_id });
    return t("numbers.unrouted");
  };
  return (
    <Page title={t("nav.numbers")} description={t("numbers.description")}>
      <PhoneLineDialog open={needsLine} onOpenChange={setNeedsLine} />
      <Section query={numbers}>
        {(page) =>
          page.data.length === 0 ? (
            <Empty title={t("numbers.empty")}>
              <p>{t("numbers.emptyBody")}</p>
              {hasLine === false && (
                <Button onClick={() => setNeedsLine(true)}>{t("line.connect")}</Button>
              )}
            </Empty>
          ) : (
            <Table
              caption={t("nav.numbers")}
              rows={page.data}
              rowKey={(n) => n.id}
              columns={[
                { header: t("numbers.col.number"), cell: (n) => <span className="font-mono">{n.e164}</span> },
                {
                  header: t("numbers.col.answeredBy"),
                  cell: (n) => (canWrite ? <Target number={n} agents={agentList} scripts={scriptList} /> : answeredBy(n)),
                },
                { header: t("numbers.col.enabled"), cell: (n) => t(n.enabled ? "common.yes" : "common.no") },
              ]}
            />
          )
        }
      </Section>
    </Page>
  );
}

/** Choose the agent or the Call Script app a number routes to, then save. */
function Target({ number, agents, scripts }: { number: PhoneNumber; agents: Named[]; scripts: Named[] }) {
  const t = useT();
  const { api } = useBff();
  const tenant = useMe().tenant_id;
  const queries = useQueryClient();
  const [value, setValue] = useState(targetOf(number));
  const save = useMutation({
    mutationFn: () => data(api.PATCH("/v1/phone-numbers/{number_id}", {
      params: { path: { number_id: number.id } },
      body: value.startsWith("scr_") ? { script_app_id: value } : { agent_id: value },
    })),
    onSuccess: () => queries.invalidateQueries({ queryKey: ["phone-numbers", tenant] }),
  });
  const changed = value !== targetOf(number) && value !== "";
  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center gap-2">
        <select aria-label={t("numbers.targetFor", { number: number.e164 })} value={value} onChange={(e) => setValue(e.target.value)}
          className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1.5 text-sm">
          {targetOf(number) === "" && <option value="">{t("numbers.unrouted")}</option>}
          <optgroup label={t("nav.agents")}>
            {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </optgroup>
          <optgroup label={t("nav.scripts")}>
            {scripts.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </optgroup>
        </select>
        {changed && (
          <Button variant="secondary" disabled={save.isPending} onClick={() => save.mutate()}
            aria-label={t("numbers.saveFor", { number: number.e164 })}>
            {t("numbers.save")}
          </Button>
        )}
      </div>
      {save.isError && <ErrorState error={save.error} />}
    </div>
  );
}
